import React from 'react';
import get from 'lodash/get';
import { Route, Switch, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Flex } from '@rebass/grid';
import { palette } from 'styled-theme';
import styled from 'styled-components';
import { prop } from 'styled-tools';
import HomePage from './components/pages/Home';
import TAndCPage from './components/pages/TAndC';
import SuccessPage from './components/pages/Success';
import RedeemPage from './components/pages/Redeem';
import CompletedPage from './components/pages/Completed';
import Image from './components/atoms/Image';
import Text from './components/atoms/Text';
import { LoadingProvider } from './context/loading';
import { AuthProvider, initialAuthState } from './context/auth';
import { initialStampData, StampDataProvider } from './context/stampData';
import { createUser } from './api/auth';
import { getCoupons } from './api/coupon';
import { getStampCollection, getUserStamps } from './api/stamp';
import { getStampCollectionId, getCouponTemplateId, getUserStatus, isDateValid } from './utils/common';
import headerImg from  './assets/images/header.png';
import bg_tAndC from './assets/images/bg_tAndC.jpg';
import bg_success from './assets/images/bg_success.jpg';
import bg_completed from './assets/images/bg_completed.jpg';
import bg_home from './assets/images/bg_home.jpg';

const checkBrowser = () => {
  const { userAgent, platform, maxTouchPoints } = window.navigator;
  const platformHasIPhone = platform.includes('iPhone');
  const userAgentHasIPhone = userAgent.includes('iPhone');
  const platformHasIPad = platform.includes('iPad');
  const userAgentHasIPad = userAgent.includes('iPad');
  const userAgentLikeMac = userAgent.includes('like Mac');
  const isIPad = platformHasIPad || userAgentHasIPad;
  const isIPhone = platformHasIPhone || userAgentHasIPhone;
  const isIOS = isIPad || isIPhone || userAgentLikeMac || (platform === 'MacIntel' && maxTouchPoints > 1)
  const isChrome = userAgent.includes('Chrome') || userAgent.includes('CriOS');
  const isFirefox = userAgent.includes('Firefox') || userAgent.includes('FxiOS');
  const isOpera = userAgent.includes('Opera') || userAgent.includes('OP');
  const isSafari = userAgent.includes('Safari') && !isChrome && !isFirefox && !isOpera && (!isIOS || (isIOS && (userAgentHasIPad || userAgentHasIPhone)));
  if (isIOS && !isSafari) {
    alert('For iOS users, it is requried to use Safari to run the application');
  }
};

const Container = styled.div`
  height: 100%;
  background: url(${prop('background')}) no-repeat top;
  background-color: ${palette('secondary', 0)};
  background-size: 100% 100%;
  background-attachment: fixed;
  overflow: auto;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin-bottom: -1rem;
`;

const HeaderTitle = styled(Text)`
  position: absolute;
  top: 0.5rem;
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 22px 4px;
`;

const ViewPort = styled(Flex)`
  flex-direction: column;
  max-width: 640px;
  width: 100%;
  height: 100%;
  margin: auto;
`;

const LoaderContainer = styled(Flex)`
  background: rgba(0, 0, 0, 0.6);
  justify-content: center;
  align-items: center;
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  top: 0;
`;

const getBackground = (pathname) => {
  switch (pathname) {
    case '/terms':
      return bg_tAndC;
    case '/success':
      return bg_success;
    case '/redeem':
    case '/completed':
      return bg_completed;
    default:
      return bg_home;
  }
};

const getStoredAuth = (type) => {
  let storedAuth = null;
  if (localStorage.getItem(`auth_${type}`)) {
    try {
      storedAuth = JSON.parse(localStorage.getItem(`auth_${type}`));
    } catch (err) {
      storedAuth = null;
    }
  }
  return storedAuth;
};

const parseAuthData = (authData) => {
  const { id_token, ...remainData } = authData;
  try {
    const user = JSON.parse(atob(id_token.split('.')[1]));
    remainData.user = user;
  } catch (err) {
    console.error('err', err);
  }
  return remainData;
};

class App extends React.Component {
  constructor(props) {
    super(props);
    const { type } = props;
    this.state = {
      auth: getStoredAuth(type) || initialAuthState,
      stampCollection: initialStampData.stampCollection,
      userStamps: initialStampData.userStamps,
      loading: true,
    };
  }

  setAuth = (newAuth) => {
    const { type } = this.props;
    this.setState({ auth: newAuth });
    localStorage.setItem(`auth_${type}`, JSON.stringify(newAuth));
  }

  setStampCollection = (stampCollection) => {
    this.setState({ stampCollection });
  }

  setUserStamps = (userStamps) => {
    this.setState({ userStamps });
  }

  setLoading = (isLoading) => {
    this.setState({ loading: isLoading });
  }

  loadAuth = () => {
    const { type } = this.props;
    const storedAuth = getStoredAuth(type);
    if (!(storedAuth && storedAuth.access_token)) {
      return createUser()
        .then(({ data }) => {
          const parsedAuth = parseAuthData(data);
          this.setAuth(parsedAuth);
          return parsedAuth;
        });
    }
    return Promise.resolve(storedAuth);
  }

  loadStampData = (auth) => {
    const { type } = this.props;
    if (auth && auth.access_token && auth.user) {
      const stampCollectionId = getStampCollectionId(type);
      const couponTemplate = getCouponTemplateId(type);
      const stampCollectionParams = {
        '$expand': 'stamp_templates/stamp_marker_file,stamp_templates/unscanned_icon,stamp_templates/scanned_icon',
      };
      const userStampsParams = {
        'stamp_template/stamp_collection': stampCollectionId,
      };
      const couponParams = {
        owner_id: get(auth, 'user.username'),
        coupon_template: couponTemplate,
      };
      return Promise.all([
        getStampCollection(stampCollectionId, stampCollectionParams, auth, this.setAuth),
        getUserStamps(userStampsParams, auth, this.setAuth),
        getCoupons(couponParams, auth, this.setAuth),
      ])
        .then(([{ data: stampCollection }, { data: userStamps }, { data: coupons }]) => {
          this.setStampCollection(stampCollection);
          this.setUserStamps(userStamps);
          const userStatus = getUserStatus(stampCollection, userStamps, get(coupons, 0));
          if (userStatus !== auth.user.status) {
            const updatedUser = { ...auth.user, status: userStatus };
            this.setAuth({ ...auth, user: updatedUser });
          }
        });
    }
  }

  checkRoute = () => {
    const { location, history } = this.props;
    const { auth } = this.state;
    if (auth && auth.user) {
      const status = auth.user.status;
      if (status === 'success') {
        if (!['/success', '/redeem'].includes(location.pathname)) {
          history.push('/success');
        }
      } else if (status === 'completed') {
        if (location.pathname !== '/completed') {
          history.push('/completed');
        }
      } else if (!['/', '/terms'].includes(location.pathname)) {
        history.push('/');
      }
    } else if (location.pathname !== '/terms') {
      history.push('/terms');
    }
  }

  withAppType = (Component) => {
    const { type } = this.props;
    return ({ ...props }) => (
      <Component appType={type} {...props} />
    );
  }

  componentDidMount() {
    checkBrowser();
    this.checkRoute();
    Promise.resolve(isDateValid() ? this.loadAuth() : null)
      .then((loadedAuth) => this.loadStampData(loadedAuth))
      .catch(err => alert(err.message))
      .finally(() => this.setLoading(false));
  }

  componentDidUpdate(prevProps, prevState) {
    const { auth } = this.state;
    const { location } = this.props;
    if (get(prevState.auth, 'user.status') !== get(auth, 'user.status')
      || prevProps.location.pathname !== location.pathname) {
      this.checkRoute();
    }
  }

  render() {
    const { type, location }  = this.props;
    const { auth, stampCollection, userStamps, loading }  = this.state;
    const headerTitle = type === 'kolour_tw' ? '荃灣千色匯' : '元朗千色匯';
    return (
      <AuthProvider value={{ auth, setAuth: this.setAuth }}>
        <LoadingProvider value={{ loading, setLoading: this.setLoading }}>
          <StampDataProvider value={{ stampCollection, userStamps, setStampCollection: this.setStampCollection, setUserStamps: this.setUserStamps }}>
            <Container background={getBackground(location.pathname)}>
              <ViewPort>
                <Header>
                  <HeaderTitle variant="h2" palette="secondary" align="center">{headerTitle}</HeaderTitle>
                  <Image src={headerImg} width="100%" />
                </Header>
                <Flex flex="1 1 0" justifyContent="center">
                  <Switch>
                    <Route path='/terms' exact component={TAndCPage} />
                    <Route path='/redeem' exact render={this.withAppType(RedeemPage)} />
                    <Route path='/success' exact render={this.withAppType(SuccessPage)} />
                    <Route path='/completed' exact render={this.withAppType(CompletedPage)} />
                    <Route path='/' exact render={this.withAppType(HomePage)} />
                  </Switch>
                </Flex>
                <Footer>
                  <Box>
                    <Text variant="body2" fontType="quaternary">&#9426;	2020 Nina, What About</Text>
                  </Box>
                  <Box ml="1rem">
                    <Text variant="body2" fontType="quaternary">&#9426;	Bigsoil All rights reserved</Text>
                  </Box>
                </Footer>
              </ViewPort>
              {loading && (
                <LoaderContainer>
                  <Text variant="h3" palette="secondary">Loading...</Text>
                </LoaderContainer>
              )}
            </Container>
          </StampDataProvider>
        </LoadingProvider>
      </AuthProvider>
    );
  }
}

SuccessPage.propTypes = {
  type: PropTypes.string,
  history: PropTypes.object,
  location: PropTypes.object,
};

SuccessPage.defaultProps = {
  type: 'kolour_tw',
  history: {},
  location: {},
};

export default withRouter(App);
